import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { postToHost } from './shared.js';
import { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — self-update modals + progress,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export type UpdateInfoForUI = {
  installed_version: string;
  latest_version: string;
  download_url: string;
  sha256: string;
  size_bytes: number;
  changelog: string[];
  forced: boolean;
  force_below_version?: string | null;
};
export type UpdateProgressForUI = {
  phase: 'downloading' | 'verifying' | 'extracting' | 'starting' | 'error';
  percent?: number;
  downloaded?: number;
  total?: number;
  message?: string;
};

export function formatMB(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

export function ProgressView({ progress }: { progress: UpdateProgressForUI }): React.JSX.Element {
  const pct = progress.percent ?? 0;

  // Round 28-AC follow-up: ordered phase timeline so the user knows where
  // we are vs. where we're going. The 'starting' phase is the longest +
  // most opaque (Outlook needs to close, the updater takes over, install
  // runs, Outlook relaunches) — we add an elapsed-time counter and a
  // helpful hint after 30s ("close Outlook manually if it hasn't closed").
  const PHASES: Array<{ key: UpdateProgressForUI['phase']; label: string }> = [
    { key: 'downloading', label: 'Download' },
    { key: 'verifying', label: 'Verify' },
    { key: 'extracting', label: 'Extract' },
    { key: 'starting', label: 'Install + restart' },
  ];
  const currentIdx = PHASES.findIndex((p) => p.key === progress.phase);
  const isError = progress.phase === 'error';
  const phaseDetail: Record<UpdateProgressForUI['phase'], string> = {
    downloading:
      progress.percent != null ? `Downloading the new version (${pct}%)…` : 'Downloading…',
    verifying: 'Verifying file integrity (SHA-256)…',
    extracting: 'Extracting the update package…',
    starting:
      'Closing Outlook, installing in the background, then Outlook will reopen on the new version.',
    error: 'Update failed',
  };

  // Elapsed counter for the "starting" (= "Install + restart") phase. This
  // is where the user spent silent time in v3.5.42 — the addin spawned the
  // updater, called _app.Quit(), and there was no visible feedback during
  // the 1.5s grace + Outlook's actual close + install.bat + relaunch.
  const [elapsedSec, setElapsedSec] = useState(0);
  const startingRef = useRef<number | null>(null);
  useEffect(() => {
    if (progress.phase === 'starting') {
      if (startingRef.current == null) {
        startingRef.current = Date.now();
        setElapsedSec(0);
      }
      const id = window.setInterval(() => {
        if (startingRef.current != null) {
          setElapsedSec(Math.floor((Date.now() - startingRef.current) / 1000));
        }
      }, 1000);
      return () => window.clearInterval(id);
    }
    startingRef.current = null;
    setElapsedSec(0);
    return undefined;
  }, [progress.phase]);

  return (
    <Stack gap="sm">
      {/* Phase timeline: pill row showing done → current → pending */}
      {!isError && (
        <Group gap={4} wrap="nowrap">
          {PHASES.map((p, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            return (
              <Group key={p.key} gap={4} wrap="nowrap" style={{ flex: 1 }}>
                <Badge
                  size="sm"
                  variant={active ? 'filled' : done ? 'light' : 'outline'}
                  color={active ? 'blue' : done ? 'green' : 'gray'}
                  style={{ flexShrink: 0 }}
                >
                  {done ? '✓' : i + 1}
                </Badge>
                <Text
                  size="xs"
                  fw={active ? 600 : 400}
                  {...(active ? {} : { c: 'dimmed' })}
                  truncate
                >
                  {p.label}
                </Text>
              </Group>
            );
          })}
        </Group>
      )}

      <Group gap="xs">
        {!isError && <Loader size="sm" />}
        <Text size="sm" fw={500}>{phaseDetail[progress.phase]}</Text>
      </Group>

      {progress.phase === 'downloading' && (
        <>
          <Progress value={pct} size="md" striped animated />
          {progress.total && progress.downloaded != null && (
            <Text size="xs" c="dimmed">
              {formatMB(progress.downloaded)} / {formatMB(progress.total)}
            </Text>
          )}
        </>
      )}

      {progress.phase === 'starting' && (
        <Alert color="blue" variant="light">
          <Stack gap={6}>
            <Text size="sm">
              {progress.message ??
                'Outlook will close in a moment, the new version will install in the background, then Outlook reopens automatically.'}
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">
                Waiting for Outlook to close — {elapsedSec}s elapsed
              </Text>
            </Group>
            {elapsedSec >= 30 && (
              <Text size="xs" c="orange" fw={500}>
                ⚠ Outlook hasn't closed on its own yet. Click File → Exit (or
                close the Outlook window) — the installer is patiently waiting
                in the background and will resume the moment Outlook is gone.
                You won't lose anything.
              </Text>
            )}
            {elapsedSec >= 90 && (
              <Text size="xs" c="dimmed">
                Still not progressing? The installer log is at
                <Text component="span" ff="monospace" size="xs">
                  {' '}%APPDATA%\ai-outlook\updater.log
                </Text>{' '}— share it with support if needed.
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      {progress.phase === 'error' && (
        <Alert color="red" variant="light" title="Update failed">
          <Text size="sm">{progress.message ?? 'Unknown error.'}</Text>
        </Alert>
      )}
    </Stack>
  );
}

export function ChangelogList({ items }: { items: string[] }): React.JSX.Element | null {
  if (!items || items.length === 0) return null;
  return (
    <Stack gap={4}>
      <Text size="xs" fw={600} c="dimmed">What's new:</Text>
      <Stack gap={2}>
        {items.slice(0, 6).map((c, i) => (
          <Text key={i} size="xs" c="dimmed">• {c}</Text>
        ))}
      </Stack>
    </Stack>
  );
}

export function UpdateAvailableModal(props: {
  info: UpdateInfoForUI | null;
  progress: UpdateProgressForUI | null;
  dismissed: boolean;
  onDismiss: () => void;
}): React.JSX.Element {
  const { info, progress, dismissed, onDismiss } = props;
  const installing = !!progress && progress.phase !== 'error';
  function applyUpdate() {
    if (!info) return;
    postToHost({
      type: 'apply_update',
      download_url: info.download_url,
      sha256: info.sha256,
    });
  }
  return (
    <Modal
      opened={!!info && !dismissed}
      onClose={() => { if (!installing) onDismiss(); }}
      title="Update available"
      withCloseButton={!installing}
      closeOnClickOutside={!installing}
      closeOnEscape={!installing}
      size="md"
    >
      {info && !progress && (
        <Stack gap="md">
          <Group gap="xs">
            <Badge color="blue" variant="light">v{info.latest_version}</Badge>
            <Text size="sm" c="dimmed">You're on v{info.installed_version}</Text>
            {info.size_bytes > 0 && <Text size="sm" c="dimmed">· {formatMB(info.size_bytes)}</Text>}
          </Group>
          <ChangelogList items={info.changelog} />
          <Text size="xs" c="dimmed">
            Downloads in the background, then Outlook closes and re-opens automatically with the new version. Takes about a minute.
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onDismiss}>Remind me later</Button>
            <Button onClick={applyUpdate}>Update now</Button>
          </Group>
        </Stack>
      )}
      {info && progress && <ProgressView progress={progress} />}
    </Modal>
  );
}

export function UpdateBlockerOverlay(props: {
  info: UpdateInfoForUI;
  progress: UpdateProgressForUI | null;
}): React.JSX.Element {
  const { info, progress } = props;
  function applyUpdate() {
    postToHost({
      type: 'apply_update',
      download_url: info.download_url,
      sha256: info.sha256,
    });
  }
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <Card padding="xl" radius="md" withBorder shadow="lg" style={{ maxWidth: 520, width: '100%', background: 'white' }}>
        <Stack gap="md">
          <Group gap="sm">
            <Badge color="red" variant="filled" size="lg">REQUIRED</Badge>
            <Title order={4}>Update required to continue</Title>
          </Group>
          <Text size="sm">
            You're running <b>v{info.installed_version}</b>. The addin will not work until you update to <b>v{info.latest_version}</b>.
          </Text>
          {info.force_below_version && (
            <Text size="xs" c="dimmed">
              Versions older than v{info.force_below_version} are blocked because they have a critical fix you must adopt before continuing.
            </Text>
          )}
          <ChangelogList items={info.changelog} />
          {!progress && (
            <>
              <Text size="xs" c="dimmed">
                Updates take about a minute. Outlook will close and re-open automatically.
              </Text>
              <Button onClick={applyUpdate} size="md" fullWidth>Update now</Button>
            </>
          )}
          {progress && <ProgressView progress={progress} />}
        </Stack>
      </Card>
    </div>
  );
}

// Round 37: real SVG icons throughout. Stroke 1.5 + size 16 reads as
// professional UI rather than emoji-decoration.
